/**
 * Bekleyen Extra Mesai Talepleri — BOSS / Yönetici Genel Bakış feed kartı.
 *
 * autoCheckoutCron, yetkili personel çıkış saatini +30 dk aşınca
 * `extraMesaiTalepleri` kaydı oluşturur (durum:'bekliyor'). Bu kart o
 * talepleri listeler; BOSS/yönetici Onayla / Reddet eder.
 *
 *  - Onayla → extra ödenir (maas-hesap çıkış saatinden sonrasını sayar)
 *  - Reddet → extra ödenmez + kayıt tavan anına otomatik çıkışla kapatılır
 *
 * `?extra=ID` URL paramı geldiğinde o satır turuncu yanıp söner
 * (devamsız kartının vurgu paterninin aynısı: sessionStorage'da tutulur).
 */

import { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Clock4, Check, X, AlertTriangle } from 'lucide-react'
import {
  bekleyenExtraTalepler,
  extraOnayla,
  extraReddet,
  type ExtraMesaiTalebi,
} from '@/lib/services/extra-mesai-service'
import type { Sube } from '@/types'

/** Talebin giriş gününe göre "Bugün / Dün / N gün önce" + eski mi. */
function gunEtiketi(tarih: string): { metin: string; eski: boolean } {
  const bugun = new Date()
  bugun.setHours(0, 0, 0, 0)
  const [y, m, d] = tarih.split('-').map(Number)
  const t = new Date(y!, m! - 1, d!)
  t.setHours(0, 0, 0, 0)
  const fark = Math.round((bugun.getTime() - t.getTime()) / 86400000)
  if (fark <= 0) return { metin: 'Bugün', eski: false }
  if (fark === 1) return { metin: 'Dün', eski: true }
  return { metin: `${fark} gün önce`, eski: true }
}

function saatEt(ms: number): string {
  return new Date(ms).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })
}

const VURGU_KEY = 'extraVurguId'
const VURGU_STIL = `
@keyframes extraYanip {
  0%, 100% { background-color: rgb(255 237 213); border-color: rgb(253 186 116); }
  50% { background-color: rgb(254 215 170); border-color: rgb(251 146 60); }
}
.extra-vurgu { animation: extraYanip 1.2s ease-in-out infinite; }
`

interface Props {
  /** Onay yapan kişinin uid'i (BOSS veya yönetici) */
  onaylayanId: string
  subeler: Sube[]
  /** Yönetici için: yalnız bu şubelerin talepleri; BOSS için null = hepsi */
  subeFiltre: string[] | null
  onYenile?: () => void
}

export function BekleyenExtraKart({ onaylayanId, subeler, subeFiltre, onYenile }: Props) {
  const [talepler, setTalepler] = useState<ExtraMesaiTalebi[]>([])
  const [yukleniyor, setYukleniyor] = useState(true)
  const [islemId, setIslemId] = useState<string | null>(null)
  const [vurguId, setVurguId] = useState<string | null>(null)
  const [ekSaat, setEkSaat] = useState<Record<string, number>>({})
  const [searchParams, setSearchParams] = useSearchParams()

  useEffect(() => {
    const urlId = searchParams.get('extra')
    if (urlId) {
      sessionStorage.setItem(VURGU_KEY, urlId)
      const yeni = new URLSearchParams(searchParams)
      yeni.delete('extra')
      setSearchParams(yeni, { replace: true })
    }
    const kayitli = sessionStorage.getItem(VURGU_KEY)
    if (kayitli) setVurguId(kayitli)
  }, [searchParams, setSearchParams])

  async function yukle() {
    setYukleniyor(true)
    try {
      const hepsi = await bekleyenExtraTalepler()
      const filtreli =
        subeFiltre === null
          ? hepsi
          : hepsi.filter((t) => subeFiltre.includes(t.subeId))
      setTalepler(filtreli)
    } catch {
      setTalepler([])
    } finally {
      setYukleniyor(false)
    }
  }

  useEffect(() => {
    yukle()
    // subeFiltre referansı her render değişebilir; içeriğe göre tetikle
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(subeFiltre)])

  useEffect(() => {
    if (!vurguId) return
    const el = document.getElementById(`extra-${vurguId}`)
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }, [vurguId, talepler])

  const subeMap = useMemo(() => {
    const m = new Map<string, Sube>()
    subeler.forEach((s) => m.set(s.id, s))
    return m
  }, [subeler])

  function vurguyuDurdur() {
    sessionStorage.removeItem(VURGU_KEY)
    setVurguId(null)
  }

  async function onayla(t: ExtraMesaiTalebi) {
    const saat = ekSaat[t.id] ?? 1
    if (!(saat > 0)) {
      alert('Onaylanan extra süre 0\'dan büyük olmalı.')
      return
    }
    vurguyuDurdur()
    setIslemId(t.id)
    try {
      await extraOnayla(t.id, onaylayanId, Math.round(saat * 60))
      await yukle()
      onYenile?.()
    } catch (e) {
      alert((e as Error).message ?? 'Onay başarısız.')
    } finally {
      setIslemId(null)
    }
  }

  async function reddet(t: ExtraMesaiTalebi) {
    vurguyuDurdur()
    setIslemId(t.id)
    try {
      await extraReddet(t.id, onaylayanId)
      await yukle()
      onYenile?.()
    } catch (e) {
      alert((e as Error).message ?? 'Red başarısız.')
    } finally {
      setIslemId(null)
    }
  }

  if (yukleniyor) return null
  if (talepler.length === 0) return null

  return (
    <>
      <style>{VURGU_STIL}</style>
      <div className="card border-2 border-orange-200 bg-orange-50/50">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
            <Clock4 className="w-5 h-5 text-orange-600" />
            Extra Mesai Onayı Bekliyor ({talepler.length})
          </h2>
        </div>

        <div className="space-y-2">
          {talepler.map((t) => {
            const sube = subeMap.get(t.subeId)
            const isVurgu = vurguId === t.id
            const tarihEt = t.tarih.split('-').reverse().join('.')
            const gun = gunEtiketi(t.tarih)
            const girisSaati = saatEt(t.girisMs)
            const bitisSaati = saatEt(t.tavanMs)
            const islemde = islemId === t.id
            return (
              <div
                id={`extra-${t.id}`}
                key={t.id}
                className={`bg-white rounded-xl border-2 p-3 transition-colors ${
                  isVurgu
                    ? 'extra-vurgu'
                    : gun.eski
                      ? 'border-amber-300 bg-amber-50/60'
                      : 'border-orange-100'
                }`}
              >
                <div>
                  {/* İsim + gün rozeti */}
                  <div className="flex items-start gap-2">
                    <p className="font-semibold text-gray-900 flex-1 break-words">
                      {t.personelIsim}
                    </p>
                    <span
                      className={`text-[10px] px-2 py-0.5 rounded-full font-bold uppercase inline-flex items-center gap-0.5 shrink-0 ${
                        gun.eski
                          ? 'bg-amber-200 text-amber-800'
                          : 'bg-green-100 text-green-700'
                      }`}
                    >
                      {gun.eski && <AlertTriangle className="w-2.5 h-2.5" />}
                      {gun.metin}
                    </span>
                  </div>

                  {/* Bilgi satırları */}
                  <div className="mt-2 space-y-0.5 text-sm text-gray-700">
                    <p>
                      {tarihEt}
                      {sube && <span className="text-gray-500"> · {sube.isim}</span>}
                    </p>
                    <p>
                      <span className="text-gray-500">giriş</span> {girisSaati}
                      <span className="text-gray-400"> → </span>
                      <span className="text-gray-500">vardiya bitişi</span> {bitisSaati}
                    </p>
                  </div>

                  <p className="text-xs text-gray-500 mt-2">
                    Kaç saat extra onaylıyorsun? Süre dolunca sistem otomatik çıkış yapar.
                  </p>
                  {gun.eski && (
                    <p className="text-xs text-amber-700 mt-1 font-medium">
                      Eski talep — güncel mesai değilse reddet.
                    </p>
                  )}

                  {/* Aksiyon satırı — tam genişlik */}
                  <div className="flex items-stretch gap-2 mt-3">
                    <div className="flex items-center gap-1 bg-gray-50 border border-gray-200 rounded-lg px-2 shrink-0">
                      <input
                        type="number"
                        min={0.5}
                        step={0.5}
                        value={ekSaat[t.id] ?? 1}
                        onChange={(e) =>
                          setEkSaat((s) => ({ ...s, [t.id]: Number(e.target.value) }))
                        }
                        className="w-10 text-sm text-right bg-transparent outline-none py-2"
                        title="Onaylanan extra süre (saat)"
                      />
                      <span className="text-xs text-gray-500">saat</span>
                    </div>
                    <button
                      onClick={() => reddet(t)}
                      disabled={islemde}
                      className="flex-1 inline-flex items-center justify-center gap-1 px-3 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-semibold rounded-lg transition-colors disabled:opacity-50"
                    >
                      <X className="w-4 h-4" />
                      Reddet
                    </button>
                    <button
                      onClick={() => onayla(t)}
                      disabled={islemde}
                      className="flex-1 inline-flex items-center justify-center gap-1 px-3 py-2.5 bg-green-600 hover:bg-green-700 text-white text-sm font-semibold rounded-lg shadow-sm transition-colors disabled:opacity-50"
                    >
                      <Check className="w-4 h-4" />
                      Onayla
                    </button>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </>
  )
}
